import { useState } from "react";
import { Link, Plus } from "lucide-react";
import { SubTask, SubtaskLink } from "../../../types";
import { useAddSubtaskLink } from "../../../hooks/mutations/useAddLink";
import { useDeleteSubtaskLink } from "../../../hooks/mutations/useDeleteLink";
import { useUpdateSubtaskLink } from "../../../hooks/mutations/useUpdateLink";
import { LinkList } from "../../forms/LinkList";
import LinkForm from "./LinkForm";

export interface SubtaskLinksSectionProps {
  /** The sub-task whose links are being listed and edited. */
  subtask: SubTask;
  /** Patch fields on the open sub-task. Persistence is the dialog's job. */
  onChange: (updates: Partial<SubTask>) => void;
}

/**
 * The links block of the sub-task detail pane — the list, the add/edit form and
 * the three writes behind them. Owns which link (if any) the form is editing;
 * that state is local because nothing outside this block reads it.
 */
export default function SubtaskLinksSection({
  subtask,
  onChange,
}: SubtaskLinksSectionProps) {
  const addSubtaskLink = useAddSubtaskLink();
  const deleteSubtaskLink = useDeleteSubtaskLink();
  const updateSubtaskLink = useUpdateSubtaskLink();

  const [showLinkForm, setShowLinkForm] = useState(false);
  // The link whose row was opened for editing — it seeds the form's fields.
  const [editingLink, setEditingLink] = useState<SubtaskLink | null>(null);

  const handleSubmitLink = async (
    normalizedUrl: string,
    description: string,
  ) => {
    if (editingLink) {
      // Patch the row in place. This branch did not exist before: submitting
      // the pre-filled edit form fell through to the add path below, which
      // inserted a second link and left the original untouched.
      const success = await updateSubtaskLink(editingLink.id, {
        url: normalizedUrl,
        description,
      });
      if (success) {
        onChange({
          links: subtask.links?.map((l) =>
            l.id === editingLink.id
              ? { ...l, url: normalizedUrl, description }
              : l,
          ),
        });
      }
    } else {
      const link = await addSubtaskLink(subtask.id, normalizedUrl, description);
      if (link) {
        onChange({ links: [...(subtask.links || []), link] });
      }
    }
    setEditingLink(null);
    setShowLinkForm(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-warm-text-secondary flex items-center gap-2">
          <Link size={16} />
          Links
          {subtask.links && subtask.links.length > 0 && (
            <span className="text-xs text-warm-text-tertiary font-normal">
              ({subtask.links.length})
            </span>
          )}
        </div>
        {!showLinkForm && (
          <button
            type="button"
            onClick={() => setShowLinkForm(true)}
            aria-label="Add link"
            className="p-1 rounded-soft text-warm-text-secondary hover:text-warm-text-primary hover:bg-warm-bg-elevated transition-all duration-150 ease-in-out"
          >
            <Plus size={16} />
          </button>
        )}
      </div>

      {showLinkForm && (
        <LinkForm
          key={editingLink?.id ?? "new"}
          initialUrl={editingLink?.url}
          initialDescription={editingLink?.description}
          submitLabel={editingLink ? "Save Link" : "Add Link"}
          cancelAriaLabel={
            editingLink ? "Cancel editing link" : "Cancel adding link"
          }
          onSubmit={handleSubmitLink}
          onCancel={() => {
            setShowLinkForm(false);
            setEditingLink(null);
          }}
        />
      )}

      {subtask.links && subtask.links.length > 0 && (
        <LinkList
          links={subtask.links.map((l) => ({
            ...l,
            task_id: subtask.task_id,
          }))}
          onEdit={(linkId) => {
            const link = subtask.links?.find((l) => l.id === linkId);
            if (link) {
              setEditingLink(link);
              setShowLinkForm(true);
            }
          }}
          onDelete={async (linkId) => {
            await deleteSubtaskLink(linkId);
            onChange({
              links: subtask.links?.filter((l) => l.id !== linkId),
            });
          }}
          className="mt-2"
        />
      )}
    </div>
  );
}
